/**
 * agendaFind — lets Ctrl+F search the agenda panel.
 *
 * WHY (Walter 2026-08-23, shipped 2026-08-24). His original find report was
 * *"search doesn't work on this mode"* while looking at the Reminders panel.
 * Lists came first (#186/#193/#194); this is the agenda half.
 *
 * The agenda isn't a text buffer — it's a list of React rows whose text comes
 * from `reminder.text`, rendered as PLAIN text. So unlike the CodeMirror case
 * there's no hidden markup and no offset mapping: what's on screen is the
 * string, and a hit can be highlighted by wrapping a substring.
 *
 * Two directions of traffic, hence this module:
 *   - the panel PUBLISHES its rows (id + text, in display order) so the find
 *     bar can search them without reaching into the DOM;
 *   - the find bar PUBLISHES the hits it found so each row can paint its own,
 *     the same way FindOverlay paints a textarea's.
 *
 * SCOPE, Walter's call: only what the current filter tab shows. Rows hidden
 * behind "overdue"/"today"/"this week" are not searched, matching how find
 * behaves everywhere else in the app — you search what you can see.
 *
 * NOTES are in (Walter 2026-08-24, "2: in"). A row's notes sit collapsed
 * behind "+ add note", so a row holding a notes hit opens itself — otherwise
 * the count would point at something invisible, which is the complaint that
 * started this whole thread.
 */

// Visible rows in display order: [{ id, text, notes }]. Set by AgendaPanel.
let rows = [];

// id → DOM node, for scrolling a hit into view.
const els = new Map();

// What the find bar last published:
//   { query, byRow: Map(id → { text: {ranges, current}, notes: {ranges, current} }) }
// `ranges` is [{from, to}] into that field's string; `current` is the index
// within that field's ranges that is the stepped-to hit, or -1.
const EMPTY = { query: '', byRow: new Map() };
let findState = EMPTY;

const listeners = new Set();

export function setAgendaRows(next) {
  rows = Array.isArray(next) ? next : [];
}

/** Called when the panel closes — nothing is searchable any more. */
export function clearAgendaRows() {
  rows = [];
  els.clear();
}

export function getAgendaRows() {
  return rows;
}

export function registerAgendaRowEl(id, el) {
  if (el) els.set(id, el);
  else els.delete(id);
}

export function scrollAgendaRowIntoView(id) {
  try {
    els.get(id)?.scrollIntoView({ block: 'center', inline: 'nearest' });
  } catch { /* row unmounted mid-step */ }
}

export function publishAgendaFind(next) {
  findState = next || EMPTY;
  listeners.forEach((l) => { try { l(findState); } catch { /* ignore */ } });
}

export function clearAgendaFind() {
  publishAgendaFind(EMPTY);
}

export function subscribeAgendaFind(cb) {
  listeners.add(cb);
  cb(findState);
  return () => { listeners.delete(cb); };
}
